import { z } from 'zod'

import type { QueueProgress } from './dunning'
import { DEFAULT_TIME_ZONE, localDateIn, zonedParts } from './reminders'

/**
 * Notificações internas (sprint-7-spec §1): enums, preferências e funções puras. Tudo aqui é para
 * usuários do sistema; nada vai para cliente.
 */

export const NOTIFICATION_KINDS = [
  'reminder',
  'alert',
  'queue',
  'campaign',
  'import',
  'system',
  'summary',
] as const
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number]
export const NotificationKindSchema = z.enum(NOTIFICATION_KINDS, {
  error: 'Escolha um tipo de notificação válido.',
})

export const NOTIFICATION_SEVERITIES = ['info', 'attention', 'critical'] as const
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number]
export const NotificationSeveritySchema = z.enum(NOTIFICATION_SEVERITIES, {
  error: 'Escolha uma severidade válida.',
})

export const NOTIFICATION_ENTITY_TYPES = [
  'customer',
  'case',
  'agreement',
  'payment',
  'campaign',
  'import',
  'reminder',
  /** Sprint 17: lead do CRM de vendas (abre `/vendas/leads/:id`). */
  'lead',
] as const
export type NotificationEntityType = (typeof NOTIFICATION_ENTITY_TYPES)[number]
export const NotificationEntityTypeSchema = z.enum(NOTIFICATION_ENTITY_TYPES)

export const DELIVERY_CHANNELS = ['inapp', 'push'] as const
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number]
export const DeliveryChannelSchema = z.enum(DELIVERY_CHANNELS)

export const DELIVERY_STATUSES = ['queued', 'sent', 'failed', 'skipped'] as const
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number]
export const DeliveryStatusSchema = z.enum(DELIVERY_STATUSES)

export const NOTIFICATION_KIND_LABELS: Readonly<Record<NotificationKind, string>> = Object.freeze({
  reminder: 'Lembretes',
  alert: 'Alertas',
  queue: 'Fila',
  campaign: 'Campanhas',
  import: 'Importações',
  system: 'Sistema',
  summary: 'Resumo',
})

export const NOTIFICATION_SEVERITY_LABELS: Readonly<Record<NotificationSeverity, string>> =
  Object.freeze({ info: 'Informativo', attention: 'Atenção', critical: 'Crítico' })

// --- Preferências --------------------------------------------------------------------------------

export const PUSH_ALERT_LEVELS = ['attention_and_critical', 'critical', 'none'] as const
export type PushAlertLevel = (typeof PUSH_ALERT_LEVELS)[number]
export const PushAlertLevelSchema = z.enum(PUSH_ALERT_LEVELS, {
  error: 'Escolha quais alertas vão por push.',
})

const hhmmSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { error: 'Use o horário no formato HH:mm.' })

export const NotificationPushPreferencesSchema = z.object({
  reminders: z.boolean(),
  alerts: PushAlertLevelSchema,
  summary: z.boolean(),
  campaigns: z.boolean(),
})
export type NotificationPushPreferences = z.infer<typeof NotificationPushPreferencesSchema>

export const NotificationPreferencesSchema = z.object({
  /** Som opt-in; o navegador só toca depois de interação na aba. */
  sound: z.boolean(),
  /** Início de expediente local (America/Sao_Paulo); nulo = sem horário (sem resumo matinal). */
  workdayStart: hhmmSchema.nullable(),
  morningSummary: z.boolean(),
  push: NotificationPushPreferencesSchema,
  popupOnOpen: z.boolean(),
})
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>

/** Padrões de preferência de pessoa (ajustáveis por ela), não regra de negócio. */
export const DEFAULT_NOTIFICATION_PREFERENCES: Readonly<NotificationPreferences> = Object.freeze({
  sound: false,
  workdayStart: '08:00',
  morningSummary: true,
  push: Object.freeze({
    reminders: true,
    alerts: 'attention_and_critical',
    summary: true,
    campaigns: false,
  }),
  popupOnOpen: true,
})

/** PATCH parcial do bloco (`PATCH /me/preferences`): qualquer subconjunto, inclusive de `push`. */
export const NotificationPreferencesPatchSchema = NotificationPreferencesSchema.extend({
  push: NotificationPushPreferencesSchema.partial(),
}).partial()
export type NotificationPreferencesPatch = z.infer<typeof NotificationPreferencesPatchSchema>

/** Bloco salvo completado com os padrões; campos inválidos voltam ao padrão. */
export function notificationPreferencesOf(value: unknown): NotificationPreferences {
  const base = DEFAULT_NOTIFICATION_PREFERENCES
  if (!value || typeof value !== 'object') return { ...base, push: { ...base.push } }
  const raw = value as Record<string, unknown>
  const pick = <T>(schema: z.ZodType<T>, input: unknown, fallback: T): T => {
    const parsed = schema.safeParse(input)
    return parsed.success ? parsed.data : fallback
  }
  const rawPush =
    raw.push && typeof raw.push === 'object' ? (raw.push as Record<string, unknown>) : {}
  return {
    sound: pick(z.boolean(), raw.sound, base.sound),
    workdayStart: pick(hhmmSchema.nullable(), raw.workdayStart, base.workdayStart),
    morningSummary: pick(z.boolean(), raw.morningSummary, base.morningSummary),
    popupOnOpen: pick(z.boolean(), raw.popupOnOpen, base.popupOnOpen),
    push: {
      reminders: pick(z.boolean(), rawPush.reminders, base.push.reminders),
      alerts: pick(PushAlertLevelSchema, rawPush.alerts, base.push.alerts),
      summary: pick(z.boolean(), rawPush.summary, base.push.summary),
      campaigns: pick(z.boolean(), rawPush.campaigns, base.push.campaigns),
    },
  }
}

/** Aplica um PATCH parcial sobre o bloco atual (mescla `push` campo a campo). */
export function mergeNotificationPreferences(
  current: unknown,
  patch: NotificationPreferencesPatch | undefined,
): NotificationPreferences {
  const base = notificationPreferencesOf(current)
  if (!patch) return base
  const defined = <T extends object>(value: T) =>
    Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as Partial<T>
  const { push, ...rest } = patch
  return { ...base, ...defined(rest), push: { ...base.push, ...defined(push ?? {}) } }
}

// --- Alertas: dedupe e cooldown ------------------------------------------------------------------

/**
 * Chave de dedupe de alerta: `alert:<regra>:<tipo>:<id>:<AAAA-MM-DD>`. `windowStart` é o início da
 * janela (data local em São Paulo quando vier como instante).
 */
export function alertDedupeKey(
  ruleKey: string,
  entityType: NotificationEntityType,
  entityId: string,
  windowStart: Date | string,
): string {
  const day =
    windowStart instanceof Date
      ? localDateIn(windowStart, DEFAULT_TIME_ZONE)
      : windowStart.slice(0, 10)
  return `alert:${ruleKey}:${entityType}:${entityId}:${day}`
}

/** Verdadeiro quando o último alerta (`lastAt`) ainda está dentro do cooldown. 0 = sem cooldown. */
export function inCooldown(
  lastAt: Date | string | null | undefined,
  cooldownDays: number,
  now: Date,
) {
  if (lastAt === null || lastAt === undefined || cooldownDays <= 0) return false
  const last = lastAt instanceof Date ? lastAt : new Date(lastAt)
  return now.getTime() - last.getTime() < cooldownDays * 86_400_000
}

// --- Push ----------------------------------------------------------------------------------------

export interface PushDecisionInput {
  kind: NotificationKind
  severity: NotificationSeverity
}

/**
 * Se a notificação vai por push pelas preferências: lembretes, resumo e campanhas pelo switch de
 * cada um; fila nunca (só tempo real); alertas, importações e sistema pelo nível de alertas.
 */
export function shouldPush(
  prefs: NotificationPreferences | null | undefined,
  notification: PushDecisionInput,
): boolean {
  const push = notificationPreferencesOf(prefs).push
  switch (notification.kind) {
    case 'reminder':
      return push.reminders
    case 'summary':
      return push.summary
    case 'campaign':
      return push.campaigns
    case 'queue':
      return false
    case 'alert':
    case 'import':
    case 'system':
      if (push.alerts === 'none') return false
      if (push.alerts === 'critical') return notification.severity === 'critical'
      return notification.severity === 'attention' || notification.severity === 'critical'
  }
}

export interface PushPayload {
  title: string
  body: string
  url: string
  tag: string
  icon: string
  badge: string
  /**
   * Sprint 16.1 (D29): aviso de severidade alta fica na tela até a pessoa agir (o service worker
   * repassa ao `showNotification`). Ausente nos demais.
   */
  requireInteraction?: true
}

export const PUSH_PAYLOAD_MAX_BYTES = 2048
export const PUSH_ICON = '/favicon.svg'
export const NOTIFICATIONS_PAGE_URL = '/notificacoes'

/** Tamanho em bytes UTF-8 (sem depender de `TextEncoder`, que não está nos tipos do pacote). */
function byteLength(value: string): number {
  let bytes = 0
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4
  }
  return bytes
}

function truncate(value: string, maxChars: number): string {
  const chars = Array.from(value)
  if (chars.length <= maxChars) return value
  return `${chars.slice(0, Math.max(0, maxChars - 1)).join('')}…`
}

/** Corpo do push (≤ 2 KB em JSON UTF-8). `tag` agrupa repetições da mesma notificação. */
export function pushPayload(
  notification: {
    id: string
    kind: NotificationKind
    title: string
    body: string
    url?: string | null
    dedupeKey?: string | null
    severity?: NotificationSeverity | null
  },
  appName: string,
): PushPayload {
  const rawUrl = notification.url ?? NOTIFICATIONS_PAGE_URL
  const payload: PushPayload = {
    title: truncate(notification.title.trim() || appName, 120),
    body: truncate(notification.body, 500),
    url: rawUrl.length <= 512 ? rawUrl : NOTIFICATIONS_PAGE_URL,
    tag: truncate(notification.dedupeKey ?? `${notification.kind}:${notification.id}`, 128),
    icon: PUSH_ICON,
    badge: PUSH_ICON,
    ...(notification.severity === 'critical' ? { requireInteraction: true as const } : {}),
  }
  let bodyChars = Array.from(payload.body).length
  while (byteLength(JSON.stringify(payload)) > PUSH_PAYLOAD_MAX_BYTES && bodyChars > 0) {
    bodyChars = Math.max(0, bodyChars - 40)
    payload.body = truncate(notification.body, bodyChars)
  }
  let titleChars = Array.from(payload.title).length
  while (byteLength(JSON.stringify(payload)) > PUSH_PAYLOAD_MAX_BYTES && titleChars > 1) {
    titleChars = Math.max(1, titleChars - 20)
    payload.title = truncate(notification.title, titleChars)
  }
  return payload
}

// --- Resumo matinal ------------------------------------------------------------------------------

export interface MorningSummaryInput {
  queue: Pick<QueueProgress, 'pending'> & Partial<QueueProgress>
  promisesOverdue: number
  installmentsDueToday: number
  remindersToday: number
  /**
   * Sprint 16.1 (D29): casos da cobradora que vencem hoje (o mesmo filtro rápido "Vence hoje" da
   * carteira). Ausente = não entra no resumo.
   */
  dueToday?: number
  yesterday: { contacts: number; promises: number; promisedAmount: string }
}

/** Filtro rápido "Vence hoje" da carteira (link do resumo matinal quando só ele tem pendência). */
export const DUE_TODAY_CASES_URL = '/carteira?rapido=due_today'

export interface MorningSummary {
  title: string
  body: string
  url: string
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

/** Resumo curto em pt-BR; "Nada pendente para hoje" quando tudo é zero. */
export function morningSummary(input: MorningSummaryInput, name: string): MorningSummary {
  const firstName = name.trim().split(/\s+/)[0] ?? ''
  const title = firstName ? `Bom dia, ${firstName}` : 'Bom dia'
  const today: string[] = []
  if (input.queue.pending > 0)
    today.push(plural(input.queue.pending, 'caso na fila', 'casos na fila'))
  if (input.promisesOverdue > 0) {
    today.push(plural(input.promisesOverdue, 'promessa vencida', 'promessas vencidas'))
  }
  if (input.installmentsDueToday > 0) {
    today.push(
      plural(
        input.installmentsDueToday,
        'parcela de acordo vence hoje',
        'parcelas de acordo vencem hoje',
      ),
    )
  }
  if (input.remindersToday > 0) {
    today.push(plural(input.remindersToday, 'lembrete para hoje', 'lembretes para hoje'))
  }
  const dueToday = input.dueToday ?? 0
  if (dueToday > 0) today.push(plural(dueToday, 'caso vence hoje', 'casos vencem hoje'))
  const { contacts, promises, promisedAmount } = input.yesterday
  const amount = Number(promisedAmount)
  const yesterdayParts: string[] = []
  if (contacts > 0) yesterdayParts.push(plural(contacts, 'contato', 'contatos'))
  if (promises > 0) {
    yesterdayParts.push(
      plural(promises, 'promessa', 'promessas') +
        (Number.isFinite(amount) && amount > 0 ? ` (${BRL.format(amount)})` : ''),
    )
  }
  const sentences: string[] = []
  sentences.push(today.length > 0 ? `Hoje: ${joinPt(today)}.` : 'Nada pendente para hoje.')
  if (yesterdayParts.length > 0) sentences.push(`Ontem: ${joinPt(yesterdayParts)}.`)
  // A fila é o ponto de partida; sem fila, mas com casos vencendo hoje, a carteira já filtrada.
  const url =
    input.queue.pending > 0 || input.promisesOverdue > 0 || input.installmentsDueToday > 0
      ? '/fila'
      : dueToday > 0
        ? DUE_TODAY_CASES_URL
        : today.length > 0
          ? '/fila'
          : NOTIFICATIONS_PAGE_URL
  return { title, body: sentences.join(' '), url }
}

function joinPt(parts: string[]): string {
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`
}

// --- Agrupamento ---------------------------------------------------------------------------------

export const NOTIFICATION_GROUP_KEYS = ['today', 'yesterday', 'this_week', 'earlier'] as const
export type NotificationGroupKey = (typeof NOTIFICATION_GROUP_KEYS)[number]

export const NOTIFICATION_GROUP_LABELS: Readonly<Record<NotificationGroupKey, string>> =
  Object.freeze({ today: 'Hoje', yesterday: 'Ontem', this_week: 'Esta semana', earlier: 'Antes' })

export interface NotificationGroup<T> {
  key: NotificationGroupKey
  label: string
  items: T[]
}

function dayNumber(date: Date, timeZone: string): number {
  const p = zonedParts(date, timeZone)
  return Math.floor(Date.UTC(p.year, p.month - 1, p.day) / 86_400_000)
}

/**
 * Agrupa por dia local: "Hoje", "Ontem", "Esta semana" (de segunda até anteontem) e "Antes";
 * só grupos com itens, na ordem de entrada. `byKind` conta todos os itens por tipo.
 */
export function groupNotifications<T extends { createdAt: Date | string; kind: NotificationKind }>(
  items: readonly T[],
  now: Date,
  timeZone: string = DEFAULT_TIME_ZONE,
): { groups: NotificationGroup<T>[]; byKind: Record<NotificationKind, number> } {
  const today = dayNumber(now, timeZone)
  const weekday = zonedParts(now, timeZone).weekday
  const weekStart = today - ((weekday + 6) % 7)
  const buckets: Record<NotificationGroupKey, T[]> = {
    today: [],
    yesterday: [],
    this_week: [],
    earlier: [],
  }
  const byKind = Object.fromEntries(NOTIFICATION_KINDS.map((kind) => [kind, 0])) as Record<
    NotificationKind,
    number
  >
  for (const item of items) {
    byKind[item.kind] += 1
    const created = item.createdAt instanceof Date ? item.createdAt : new Date(item.createdAt)
    const day = dayNumber(created, timeZone)
    if (day >= today) buckets.today.push(item)
    else if (day === today - 1) buckets.yesterday.push(item)
    else if (day >= weekStart) buckets.this_week.push(item)
    else buckets.earlier.push(item)
  }
  const groups = NOTIFICATION_GROUP_KEYS.filter((key) => buckets[key].length > 0).map((key) => ({
    key,
    label: NOTIFICATION_GROUP_LABELS[key],
    items: buckets[key],
  }))
  return { groups, byKind }
}

// --- Cursor e canal ------------------------------------------------------------------------------

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

/** base64url de texto ASCII (o cursor só tem data ISO e uuid). */
function toBase64Url(ascii: string): string {
  let out = ''
  for (let i = 0; i < ascii.length; i += 3) {
    const a = ascii.charCodeAt(i)
    const b = i + 1 < ascii.length ? ascii.charCodeAt(i + 1) : -1
    const c = i + 2 < ascii.length ? ascii.charCodeAt(i + 2) : -1
    const n = (a << 16) | ((b < 0 ? 0 : b) << 8) | (c < 0 ? 0 : c)
    out += B64.charAt((n >> 18) & 63) + B64.charAt((n >> 12) & 63)
    if (b >= 0) out += B64.charAt((n >> 6) & 63)
    if (c >= 0) out += B64.charAt(n & 63)
  }
  return out
}

function fromBase64Url(value: string): string | null {
  if (!/^[A-Za-z0-9_-]*$/.test(value) || value.length % 4 === 1) return null
  let out = ''
  for (let i = 0; i < value.length; i += 4) {
    const chunk = value.slice(i, i + 4)
    let n = 0
    for (let j = 0; j < 4; j += 1)
      n = (n << 6) | (j < chunk.length ? B64.indexOf(chunk.charAt(j)) : 0)
    out += String.fromCharCode((n >> 16) & 255)
    if (chunk.length > 2) out += String.fromCharCode((n >> 8) & 255)
    if (chunk.length > 3) out += String.fromCharCode(n & 255)
  }
  return out
}

/** Cursor opaco da lista (`createdAt,id`), em base64url. */
export function encodeNotificationCursor(entry: { createdAt: Date | string; id: string }): string {
  const iso = entry.createdAt instanceof Date ? entry.createdAt.toISOString() : entry.createdAt
  return toBase64Url(`${iso}|${entry.id}`)
}

/** Decodifica o cursor; `null` quando inválido. */
export function decodeNotificationCursor(cursor: string): { createdAt: Date; id: string } | null {
  try {
    const raw = fromBase64Url(cursor)
    if (raw === null) return null
    const [iso, id] = raw.split('|')
    if (!iso || !id) return null
    const createdAt = new Date(iso)
    if (Number.isNaN(createdAt.getTime())) return null
    if (!/^[0-9a-f-]{36}$/i.test(id)) return null
    return { createdAt, id }
  } catch {
    return null
  }
}

/** Canal Redis pub/sub dos eventos de um usuário (worker de jobs → API → SSE). */
export function notificationsChannel(userId: string): string {
  return `notifications:${userId}`
}
